import type { Event as TauriEvent } from "@tauri-apps/api/event";
import { errorToDetail, recordDesktopDiagnostic } from "./desktopDiagnostics";

export function isDesktopShell(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface DesktopWindowOptions {
  title: string;
  labelPrefix: string;
  width?: number;
  height?: number;
}

function routeForShell(route: string): string {
  const normalized = route.startsWith("/") ? route : `/${route}`;
  return isDesktopShell() ? `#${normalized}` : normalized;
}

function labelFor(prefix: string): string {
  const cleaned = prefix
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `waytide-${cleaned || "window"}-${Date.now()}`;
}

export async function openDesktopWindow(
  route: string,
  options: DesktopWindowOptions,
): Promise<void> {
  const url = routeForShell(route);
  if (!isDesktopShell()) {
    const child = window.open(url, "_blank", "noopener,noreferrer");
    if (!child) {
      recordDesktopDiagnostic({
        level: "warn",
        area: "window",
        message: "Browser popup was blocked",
        detail: url,
      });
    }
    return;
  }

  try {
    const { WebviewWindow } = await import("@tauri-apps/api/webviewWindow");
    const label = labelFor(options.labelPrefix);
    const webview = new WebviewWindow(label, {
      url,
      title: options.title,
      width: options.width ?? 1320,
      height: options.height ?? 900,
      minWidth: 960,
      minHeight: 640,
      resizable: true,
      focus: true,
      decorations: false,
    });
    void webview.once("tauri://created", () => {
      recordDesktopDiagnostic({
        level: "info",
        area: "window",
        message: "Opened desktop window",
        detail: `${options.title} -> ${url}`,
      });
    });
    void webview.once<unknown>("tauri://error", (event: TauriEvent<unknown>) => {
      recordDesktopDiagnostic({
        level: "error",
        area: "window",
        message: "Desktop window failed to open",
        detail: errorToDetail(event.payload),
      });
    });
  } catch (error) {
    recordDesktopDiagnostic({
      level: "error",
      area: "window",
      message: "Desktop window bridge failed",
      detail: errorToDetail(error),
    });
    throw error;
  }
}
